import VehicleImage from '@allesauto/core/vehicleImage';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts } from '@allesauto/utils/consts';
import { isRueschampWhiteLabel } from '@allesauto/utils/funcs.ts';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import CarGalleryOverlay from './CarGalleryOverlay';
import ThumbnailImage from './ThumbnailImage';

export interface CarGalleryProps {
  vehicleImages: SchemaTypes.VehicleImage[] | SchemaTypes.ProductImage[];
  imageBaseUrls?: string;
  isWhiteLabel?: boolean;
}

const CarGallery: FC<React.PropsWithChildren<CarGalleryProps>> = ({
  vehicleImages,
  imageBaseUrls,
  isWhiteLabel = false,
}) => {
  const intl = useIntl();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedSlide, setSelectedSlide] = useState<number>(0);
  const [thumbnailItems, setThumbnailItems] = useState<
    SchemaTypes.VehicleImage[] | SchemaTypes.ProductImage[]
  >();
  const whiteLabel = useWhiteLabel();
  const breakpoint = useScreenBreakpoint();
  const presentationModeText = useMemo(
    () => translate(intl, 'ui.USER_ACCOUNT_DETAILS.GARAGE.PRESENTATION_MODE'),
    [intl]
  );
  const mainImage = useMemo(
    () =>
      (vehicleImages &&
        (vehicleImages[0] as SchemaTypes.VehicleImage)?.externalUrlLarge) ||
      (vehicleImages[0] as SchemaTypes.ProductImage)?.url,
    [vehicleImages]
  );
  const hasPresentationText = useMemo(
    () => mainImage && mainImage.startsWith(consts.IMACA_IMAGE),
    [mainImage]
  );

  const mobileBreakpoint = breakpoint < SCREEN_BREAKPOINT.MD;
  const maxItemsDisplayed = mobileBreakpoint ? 2 : 7;

  const numberOfItemsLeft =
    vehicleImages &&
    vehicleImages.length > 0 &&
    vehicleImages.length - maxItemsDisplayed;

  const className = {
    containerClass: clsx(
      'col-end-24 col-start-2 flex flex-col pt-5px relative md:col-start-8 md:pt-0 lg:pt-[44px]',
      isRueschampWhiteLabel(whiteLabel?.merchantOrigin)
        ? 'md:col-end-19'
        : 'md:col-end-18'
    ),
    pictureContainerClass: 'cursor-pointer relative',
    thumbnailContainerClass: clsx(
      'flex flex-row justify-end w-full md:bottom-auto md:justify-center md:mt-30px md:right-auto md:static',
      'sm:justify-center sm:mt-[16px] sm:static'
    ),
    presentationText:
      'absolute bottom-0 md:right-0 md:left-auto sm:left-0 text-grey60 text-legal',
  };

  const handleClose = useCallback(() => {
    setShowDialog(false);
  }, []);

  const handleAction = useCallback((slideNumber = 0) => {
    setShowDialog(true);
    setSelectedSlide(slideNumber);
  }, []);

  const imageBase =
    imageBaseUrls ||
    process.env.IMAGE_BASE_URL ||
    process.env.NEXT_PUBLIC_IMAGE_BASE_URL ||
    '';

  useEffect(() => {
    if (vehicleImages && vehicleImages.length > 0)
      setThumbnailItems(vehicleImages.slice(1, maxItemsDisplayed));
  }, [maxItemsDisplayed, vehicleImages]);

  return (
    <>
      <div
        className={className.containerClass}
        data-style-for-print="car-gallery"
      >
        <div
          className={className.pictureContainerClass}
          onClick={() => handleAction(0)}
        >
          <VehicleImage
            filename={mainImage}
            fallbackImage="pcp_pdp"
            isWhiteLabel={isWhiteLabel}
            pdPCarsImageAfterConfiguration
          />
          {hasPresentationText && (
            <p className={className.presentationText}>{presentationModeText}</p>
          )}
        </div>
        <div
          className={className.thumbnailContainerClass}
          data-hide-for-print=""
        >
          {thumbnailItems &&
            thumbnailItems.length > 0 &&
            thumbnailItems.map((item, index: number) => (
              <ThumbnailImage
                lastItem={
                  ((vehicleImages && vehicleImages.length > 6) ||
                    mobileBreakpoint) &&
                  index === thumbnailItems.length - 1
                }
                handleAction={() => handleAction(index + 1)}
                src={
                  (item as SchemaTypes.VehicleImage)?.externalUrlLarge ||
                  (item as SchemaTypes.ProductImage)?.url ||
                  ''
                }
                key={index}
                numberOfItems={numberOfItemsLeft || 0}
                imageBaseUrls={imageBase}
              />
            ))}
        </div>
      </div>

      {vehicleImages?.length > 0 && (
        <CarGalleryOverlay
          items={vehicleImages}
          showDialog={showDialog}
          onClose={handleClose}
          selectedSlide={selectedSlide}
        />
      )}
    </>
  );
};

export default CarGallery;
